import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { GradientLoadingBox } from 'edrolo-style-guide';
import { useEffect, useRef, useState } from 'react';
import Draggable from './Draggable';
import DropZone from './DropZone';
import styles from './styles.scss';
import { useTouchScrollLock } from './useTouchScrollLock';
/* eslint-disable react/prop-types */ // TODO: add props

const DraggablePlaceholder = ({ imageWidth, imageHeight, annotation }) => (
  <div
    style={{
      width: `${(imageWidth / 100) * annotation.geometry.width}px`,
      height: `${(imageHeight / 100) * annotation.geometry.height}px`,
      top: `${(imageHeight / 100) * annotation.geometry.y}px`,
      left: `${(imageWidth / 100) * annotation.geometry.x}px`,
      position: `absolute`,
      background: `white`,
      zIndex: `1`,
    }}
  />
);

const DragAndDropArea = ({
  draggableAnnotations,
  dropzoneAnnotations,
  imageUrl,
  draggableToContainingDropzoneMap,
  onDraggableRelocated,
  dndCorrectnessMap = null,
  isPrint = false,
}) => {
  const containerRef = useRef();
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(1000);
  const [loading, setLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [printImageDimensions, setPrintImageDimensions] = useState({
    width: 0,
    height: 0,
  });

  useTouchScrollLock(isDragging);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    }),
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    if (containerRef.current) {
      const { width } = containerRef.current.getBoundingClientRect();
      setContainerWidth(width);
    }
  }, [containerRef]);

  // Preload image for print mode to get dimensions
  useEffect(() => {
    if (
      isPrint &&
      imageUrl &&
      (!printImageDimensions.width || !printImageDimensions.height)
    ) {
      const img = new Image();
      img.onload = () => {
        setPrintImageDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };
      img.src = imageUrl;
    }
  }, [
    isPrint,
    imageUrl,
    printImageDimensions.width,
    printImageDimensions.height,
  ]);

  const onImgLoad = ({ target: img }) => {
    setImageWidth(img.naturalWidth);
    setImageHeight(img.naturalHeight);
    setLoading(false);
  };

  const imageRatio = imageWidth / imageHeight;
  const scaledWidth = containerWidth;
  const scaledHeight = containerWidth / imageRatio;

  const handleDragEnd = ({ over, active, ...event }) => {
    onDraggableRelocated(active.id, over?.id);
    setIsDragging(false);
  };

  const draggablesNotInDropZones = draggableAnnotations.filter(
    (annotation) => !draggableToContainingDropzoneMap[annotation.id] // absent or undefined
  );

  const shouldDisableDnD = !!dndCorrectnessMap;

  if (isPrint) {
    // Use a fixed container width for print (e.g., 800px or calculate from available space)
    const printContainerWidth = 800;
    // Use print image dimensions if available, otherwise fall back to regular image dimensions or default ratio
    const finalImageRatio =
      printImageDimensions.width && printImageDimensions.height
        ? printImageDimensions.width / printImageDimensions.height
        : imageWidth && imageHeight
        ? imageWidth / imageHeight
        : 4 / 3;
    const printScaledWidth = printContainerWidth;
    const finalScaledHeight = printContainerWidth / finalImageRatio;

    // Get draggables in dropzones
    const draggablesInDropzones = dropzoneAnnotations.flatMap((dropA) => {
      return draggableAnnotations
        .filter(
          (dragA) => draggableToContainingDropzoneMap[dragA.id] === dropA.id
        )
        .map((draggableAnnotation) => ({
          annotation: draggableAnnotation,
          isInDropzone: true,
        }));
    });

    // Get draggables not in dropzones
    const draggablesNotInDropzones = draggableAnnotations
      .filter((annotation) => !draggableToContainingDropzoneMap[annotation.id])
      .map((annotation) => ({
        annotation,
        isInDropzone: false,
      }));

    // Combine all draggables
    const allDraggables = [
      ...draggablesInDropzones,
      ...draggablesNotInDropzones,
    ];

    const renderDraggable = (draggableAnnotation, isInDropzone) => {
      const { geometry } = draggableAnnotation;
      const xPos = `${(printScaledWidth / 100) * geometry.x}px`;
      const yPos = `${(finalScaledHeight / 100) * geometry.y}px`;
      const width = `${(printScaledWidth / 100) * geometry.width}px`;
      const height = `${(finalScaledHeight / 100) * geometry.height}px`;
      const bgPosX = `${(printScaledWidth / 100) * geometry.x * -1}px`;
      const bgPosY = `${(finalScaledHeight / 100) * geometry.y * -1}px`;

      return (
        <div
          key={draggableAnnotation.id}
          style={{
            position: 'absolute',
            top: yPos,
            left: xPos,
            width,
            height,
            backgroundImage: `url("${imageUrl}")`,
            backgroundPosition: `${bgPosX} ${bgPosY}`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: `${printScaledWidth}px ${finalScaledHeight}px`,
            WebkitPrintColorAdjust: 'exact',
            printColorAdjust: 'exact',
            colorAdjust: 'exact',
            zIndex: 1,
            border:
              !isPrint &&
              dndCorrectnessMap?.[draggableAnnotation.id] !== undefined
                ? dndCorrectnessMap[draggableAnnotation.id]
                  ? '1px solid green'
                  : '1px solid red'
                : 'none',
          }}
        />
      );
    };

    return (
      <div style={{ width: `${printScaledWidth}px` }}>
        <div
          style={{
            position: 'relative',
            width: `${printScaledWidth}px`,
            height: `${finalScaledHeight}px`,
            WebkitPrintColorAdjust: 'exact',
            printColorAdjust: 'exact',
            colorAdjust: 'exact',
          }}
        >
          {/* Background image as actual img element for print compatibility */}
          <img
            src={imageUrl}
            alt=""
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${printScaledWidth}px`,
              height: `${finalScaledHeight}px`,
              objectFit: 'contain',
              zIndex: 0,
              WebkitPrintColorAdjust: 'exact',
              printColorAdjust: 'exact',
              colorAdjust: 'exact',
            }}
          />
          {/* Render all draggables */}
          {allDraggables.map(({ annotation, isInDropzone }) =>
            renderDraggable(annotation, isInDropzone)
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      <DndContext
        onDragEnd={handleDragEnd}
        sensors={sensors}
        onDragStart={() => setIsDragging(true)}
        onDragCancel={() => setIsDragging(false)}
      >
        <img onLoad={onImgLoad} src={imageUrl} className={styles.imageLoader} />
        {loading ? (
          <GradientLoadingBox className={styles.loaderBox} />
        ) : (
          <div
            style={{
              position: `relative`,
              backgroundImage: `url("${imageUrl}")`,
              backgroundRepeat: `no-repeat`,
              width: `${scaledWidth}px`,
              height: `${scaledHeight}px`,
              backgroundSize: `${scaledWidth}px ${scaledHeight}px`,
            }}
          >
            {draggablesNotInDropZones.map((annotation) => (
              <Draggable
                key={`draggable-${annotation.id}`}
                id={annotation.id}
                annotation={annotation}
                imageWidth={scaledWidth}
                imageHeight={scaledHeight}
                imageUrl={imageUrl}
                shouldDisableDnD={shouldDisableDnD}
              />
            ))}

            {draggableAnnotations.map((annotation) => (
              <DraggablePlaceholder
                key={`cover-${annotation.id}`}
                annotation={annotation}
                imageWidth={scaledWidth}
                imageHeight={scaledHeight}
              />
            ))}

            {dropzoneAnnotations.map((dropA) => {
              const draggablesInThisDropZone = draggableAnnotations.filter(
                (dragA) =>
                  draggableToContainingDropzoneMap[dragA.id] === dropA.id
              );

              return (
                <DropZone
                  key={dropA.id}
                  annotation={dropA}
                  renderDropped={(draggableAnnotation) => (
                    <Draggable
                      key={draggableAnnotation.id}
                      id={draggableAnnotation.id}
                      annotation={draggableAnnotation}
                      imageWidth={scaledWidth}
                      imageHeight={scaledHeight}
                      imageUrl={imageUrl}
                      isCorrect={
                        dndCorrectnessMap &&
                        dndCorrectnessMap[draggableAnnotation.id]
                      }
                      shouldDisableDnD={shouldDisableDnD}
                      isInDropzone
                    />
                  )}
                  imageWidth={scaledWidth}
                  imageHeight={scaledHeight}
                  draggableAnnotationsInThisDropZone={draggablesInThisDropZone}
                />
              );
            })}
          </div>
        )}
      </DndContext>
    </div>
  );
};

export default DragAndDropArea;
